import HorizontalCard from "Components/Common/HorizontalCardGrid/HorizontalCard/HorizontalCard";
import { HorizontalCardAddItem } from "Components/Common/HorizontalCardGrid/HorizontalCard/HorizontalCardAddItem/HorizontalCardAddItem";
import HorizontalCardAttachment from "Components/Common/HorizontalCardGrid/HorizontalCard/HorizontalCardAttachment/HorizontalCardAttachment";
import HorizontalCardImage from "Components/Common/HorizontalCardGrid/HorizontalCard/HorizontalCardImage/HorizontalCardImage";
import HorizontalCardGrid from "Components/Common/HorizontalCardGrid/HorizontalCardGrid";
import ContentView from "Components/ContentView/ContentView";
import { CSSProperties } from "jss/css";
import { ChecklistItemTemplate } from "Models/Checklists/ChecklistItemTemplate";
import { ChecklistTemplate } from "Models/Checklists/ChecklistTemplate";
import { TaskTemplateStep } from "Models/Tasks/TaskTemplateStep";
import * as React from "react";
import Dotdotdot from "react-clamp";
import i18n from "Translations/i18n";
import { ChecklistInstructionsPopover } from "./ChecklistInstructionsPopover/ChecklistInstructionsPopover";
import styles from "./ChecklistItems.scss";
import { ChecklistItemsPopover } from "./ChecklistItemsPopover/ChecklistItemsPopover";

interface IProps {
    checklistTemplate: ChecklistTemplate;
    onChange: (items: ChecklistItemTemplate[], descriptionFile?: File) => void;
}

interface IState {
    items: ChecklistItemTemplate[];
    showItemsPopover: boolean;
    showInstructionsPopover: boolean;
    popoverPaneIndex: number;
    selectedItem?: ChecklistItemTemplate;
    selectedItemIndex?: number;
    selectedCard?: HTMLElement;
}

export class ChecklistItems extends React.Component<IProps, IState> {
    // Set it to 9999 to prevent a bug when duplicating a checklist, removing a item and then adding one
    private newItemId: number = 9999;
    private cardRefs: { [stepId: number]: React.RefObject<HTMLDivElement> } = {};

    constructor(props: IProps) {
        super(props);

        this.state = {
            items: props.checklistTemplate.items,
            showItemsPopover: false,
            showInstructionsPopover: false,
            popoverPaneIndex: 0
        };

        this.openItemsPopover = this.openItemsPopover.bind(this);
        this.closePopover = this.closePopover.bind(this);
        this.addItem = this.addItem.bind(this);
        this.addInstruction = this.addInstruction.bind(this);
        this.updateInstructions = this.updateInstructions.bind(this);
        this.descriptionFileUpdated = this.descriptionFileUpdated.bind(this);
        this.updateItems = this.updateItems.bind(this);
        this.showSubItems = this.showSubItems.bind(this);
        this.closeSubItems = this.closeSubItems.bind(this);
    }

    public openItemsPopover(i: number, e: React.MouseEvent) {
        e.stopPropagation();
        this.setState({
            showItemsPopover: true,
            showInstructionsPopover: false,
            popoverPaneIndex: i
        });
    }

    public openInstructionsPopover(i: number, e: React.MouseEvent) {
        e.stopPropagation();
        this.setState({
            showItemsPopover: false,
            showInstructionsPopover: true,
            popoverPaneIndex: i
        });
    }

    public closePopover() {
        this.setState({
            showItemsPopover: false,
            showInstructionsPopover: false
        });
    }

    public updateItems(items: ChecklistItemTemplate[]) {
        items.forEach((item, i) => item.positionIndex = i + 1);
        this.setState({ items });
        this.props.onChange(items);
    }

    public updateInstructions(instructions: TaskTemplateStep[], descriptionFileUrl?: string) {
        if (this.state.selectedItem) {
            instructions.forEach((instruction, i) => instruction.positionIndex = i + 1);

            const items = this.state.items;
            const selectedItemId = this.state.selectedItem.id;
            let selectedItem: ChecklistItemTemplate | undefined;
            items.forEach(item => {
                if (item.id === selectedItemId) {
                    item.steps = instructions;
                    item.descriptionFileUrl = descriptionFileUrl;
                    selectedItem = item;
                }
            });

            this.setState({
                selectedItem,
                items
            });
            this.props.onChange(items);
        }
    }

    public addItem() {
        let localItems = this.state.items;

        if (!localItems) {
            localItems = [];
        }

        localItems.push({ ...new ChecklistItemTemplate(), id: -(++this.newItemId) });
        const paneIndex = localItems.length - 1;

        this.setState({
            items: localItems,
            showItemsPopover: true,
            popoverPaneIndex: paneIndex
        });
        this.props.onChange(this.state.items);
    }

    public addInstruction() {
        if (this.state.selectedItem) {
            const items = this.state.items;
            const selectedItemId = this.state.selectedItem.id;
            let selectedItem: ChecklistItemTemplate | undefined;
            let paneIndex: number = 0;
            items.forEach(item => {
                if (item.id === selectedItemId) {
                    if (!item.steps) {
                        item.steps = [];
                    }
                    paneIndex = item.steps.push({ ...new TaskTemplateStep(), id: -(++this.newItemId) }) - 1;
                    selectedItem = item;
                }
            });

            this.setState({
                selectedItem,
                items,
                showInstructionsPopover: true,
                popoverPaneIndex: paneIndex
            });
            this.props.onChange(items);
        }
    }

    public descriptionFileUpdated(file: File) {
        const items = this.state.items;
        const item = items.find(x => this.state.selectedItem!.id === x.id);
        item!.descriptionFileUrl = `${URL.createObjectURL(file)}#/${file.name}`;
        item!.descriptionFile = file;
        this.setState({
            items
        });
        this.props.onChange(this.state.items, file);
    }

    public static getDerivedStateFromProps(nextProps: IProps, prevState: IState): Partial<IState> {
        const partialState: Partial<IState> = {};

        if (nextProps.checklistTemplate && nextProps.checklistTemplate.items && nextProps.checklistTemplate.items !== prevState.items) {
            partialState.items = nextProps.checklistTemplate.items;
        }

        return partialState;
    }

    public showSubItems(item: ChecklistItemTemplate) {
        this.setState({ selectedItem: item });
    }

    public closeSubItems() {
        this.setState({ selectedItem: undefined, selectedItemIndex: undefined });
    }

    public getPositionStyling(): CSSProperties | undefined {
        if (this.state.selectedItem) {
            const currentCard = document.getElementById(`item-card-${this.state.selectedItem.id}`);
            if (currentCard) {
                const top = parseInt(currentCard.style.transform!.split(",")[1].trim(), 10) + currentCard.getBoundingClientRect().height + 45;
                return { top };
            }
        }

        return undefined;
    }

    public getPointerPositionStyling(): CSSProperties | undefined {
        if (this.state.selectedItem) {
            const currentCard = document.getElementById(`item-card-${this.state.selectedItem.id}`);
            if (currentCard) {
                const top = parseInt(currentCard.style.transform!.split(",")[1].trim(), 10) + currentCard.getBoundingClientRect().height + 59;
                const left = currentCard.getBoundingClientRect().left + currentCard.getBoundingClientRect().width / 2 - 30;
                return { top, left };
            }
        }

        return undefined;
    }

    public getRef(item: ChecklistItemTemplate): React.RefObject<HTMLDivElement> | undefined {
        if (!this.cardRefs[item.id!]) {
            this.cardRefs[item.id!] = React.createRef();
        }
        return this.cardRefs[item.id!];
    }

    public render() {
        return (

            <div className={styles.gridContainer}>
                <div className={`${styles.subItemsOverlay} ${this.state.selectedItem ? styles.show : null}`} onClick={this.closeSubItems} />
                <div className={`${styles.subItemsPointer} ${this.state.selectedItem ? styles.show : null}`} style={this.getPointerPositionStyling()} />
                {this.state.selectedItem && (
                    <ChecklistInstructionsPopover
                        items={this.state.selectedItem.steps}
                        showItemsPopover={this.state.showInstructionsPopover}
                        closePopover={this.closePopover}
                        onUpdate={this.updateInstructions}
                        addItem={this.addInstruction}
                        popoverPaneIndex={this.state.popoverPaneIndex}
                        descriptionFileUrl={this.state.selectedItem.descriptionFileUrl} />)}
                <div className={`${styles.subItemsContainer} ${this.state.selectedItem ? styles.show : null}`} style={this.getPositionStyling()}>
                    <ContentView className={styles.subItemsContentView}>
                        {this.state.selectedItem && (
                            <HorizontalCardGrid title={i18n.t("common.instructions")} data={this.state.selectedItem.steps} onUpdate={this.updateInstructions}>
                                {this.state.selectedItem.steps && this.state.selectedItem.steps.length > 0 ? (
                                    this.state.selectedItem.steps.map((step, i) => (
                                        <HorizontalCard key={step.id} onClickEdit={(e) => this.openInstructionsPopover(i, e)}>
                                            <HorizontalCardImage src={step.videoThumbnailUrl || step.pictureUrl} />
                                            <h2><Dotdotdot clamp={2}>{step.description}</Dotdotdot></h2>
                                        </HorizontalCard>
                                    ))
                                ) : this.state.selectedItem.descriptionFileUrl ? (
                                    <HorizontalCard key="descriptionfile-card" onClickEdit={(e) => this.openInstructionsPopover(0, e)}>
                                        <HorizontalCardAttachment fileUrl={this.state.selectedItem.descriptionFileUrl} />
                                        <h2>{i18n.t("checklists.instructionPdf")}</h2>
                                    </HorizontalCard>
                                ) : (
                                            <HorizontalCardAddItem
                                                key="new-item-card"
                                                addItem={this.addInstruction}
                                                descriptionFileUpdated={this.descriptionFileUpdated}
                                            />
                                        )}
                                {this.state.selectedItem.steps && this.state.selectedItem.steps.length > 0 &&
                                    <HorizontalCardAddItem
                                        key="new-item-card"
                                        addItem={this.addInstruction}
                                    />
                                }
                            </HorizontalCardGrid>
                        )}
                    </ContentView>
                </div>
                <ContentView>
                    <ChecklistItemsPopover
                        items={this.state.items}
                        showItemsPopover={this.state.showItemsPopover}
                        closePopover={this.closePopover}
                        onUpdate={this.updateItems}
                        addItem={this.addItem}
                        popoverPaneIndex={this.state.popoverPaneIndex} />

                    <HorizontalCardGrid selectedCardKey={this.state.selectedItem ? `${this.state.selectedItem.id}` : undefined} title={i18n.t("checklists.checklistItems")} data={this.state.items} onUpdate={this.updateItems}>
                        {this.state.items && this.state.items.length > 0 ? (
                            this.state.items.map((item, i) => (
                                <HorizontalCard id={`item-card-${item.id}`} key={item.id} onClickEdit={(e) => this.openItemsPopover(i, e)} onClick={() => this.showSubItems(item)}>
                                    <HorizontalCardImage src={item.videoThumbnailUrl || item.pictureUrl} />
                                    <h2><Dotdotdot clamp={2}>{item.name}</Dotdotdot></h2>
                                </HorizontalCard>
                            ))
                        ) : (
                                <HorizontalCardAddItem
                                    key="new-item-card"
                                    addItem={this.addItem}
                                    addItemText={i18n.t("checklists.addChecklistItem")}
                                />
                            )}
                        {this.state.items && this.state.items.length > 0 &&
                            <HorizontalCardAddItem
                                key="new-item-card"
                                addItem={this.addItem}
                                addItemText={i18n.t("checklists.addChecklistItem")}
                            />
                        }
                    </HorizontalCardGrid>
                </ContentView>
            </div>
        );
    }
}


// WEBPACK FOOTER //
// ./src/Components/Pages/ChecklistTemplateDetailView/ChecklistItems/ChecklistItems.tsx